// Helpers para Sincronizações/Integrações ERP

import { DateTime } from "luxon";

// Importar imagens dos ERPs
import omieIcon from "../assets/png/omie.png";
import contaAzulIcon from "../assets/png/contaazul.png";
import niboIcon from "../assets/png/nibo.png";
import excelIcon from "../assets/png/excel.png";

// Mapear valores de ERP para nomes
export const getErpName = (erpValue) => {
  const erpNames = {
    0: "Omie",
    1: "Conta Azul",
    2: "Nibo",
    3: "Excel",
    5: "Conta Azul",
  };
  return erpNames[erpValue] || "ERP Desconhecido";
};

export const getSyncTypeLabel = (syncType) => {
  const syncTypeLabels = {
    1: "Incremental",
    2: "Completa",
  };
  return syncTypeLabels[syncType] || "Não definido";
};

export const getSyncType = (syncType) => {
  const syncTypes = {
    1: "Padrão",
    2: "Consolidado",
  };
  return syncTypes[syncType] || "Desconhecido";
};

// Mapear ERP para ícones/imagens
export const getErpIcon = (erpValue) => {
  const erpIcons = {
    0: omieIcon, // Omie
    1: contaAzulIcon, // Conta Azul
    2: niboIcon, // Nibo
    3: excelIcon, // Excel
    5: contaAzulIcon, // Conta Azul
  };
  return erpIcons[erpValue] || null;
};

export const getErpFrequency = (erpValue) => {
  const erpFrequencies = {
    0: "4x ao dia",
    1: "1x ao dia (D-1)",
    2: "4x ao dia",
    3: "Manual",
    4: "30/30 min",
    5: "1x ao dia (D-1)",
  };
  return erpFrequencies[erpValue] || "Manual";
};

// Mapear tipos de sincronização para frequência
export const getSyncFrequency = (syncType) => {
  const frequencies = {
    1: "Manual",
    2: "Diária",
    3: "Semanal",
    4: "Mensal",
  };
  return frequencies[syncType] || "Manual";
};

// Mapear status de sincronização
export const getSyncStatus = (status, externalId, erp) => {
  // If externalId exists and erp is 0, check if status is true or false
  if (externalId != null || erp === 0) {
    if (status === true) {
      return "Ativa";
    } else if (erp === 5) {
      return "Verificar Autorização";
    } else if (status === false) {
      return "Falha";
    }
  }

  const statusMap = {
    true: "Ativa",
    false: "Inativa",
    null: "Com Falha",
  };
  return statusMap[status] || "Desconhecido";
};

// Mapear ERP para cores do tema
export const getErpColor = (erpValue) => {
  const erpColors = {
    0: "#1890ff", // Omie - Azul
    1: "#52c41a", // Conta Azul - Verde
    2: "#722ed1", // Nibo - Roxo
    3: "#faad14", // Excel - Amarelo
  };
  return erpColors[erpValue] || "#d9d9d9";
};

// Validar se ERP suporta credenciais específicas
export const erpSupportsCredentials = (erpValue, credentialType) => {
  const erpCredentials = {
    0: ["appKey", "appSecret"], // Omie
    1: ["login", "senha"], // Conta Azul
    2: ["login", "senha"], // Nibo
    3: [], // Excel
  };

  const supportedCredentials = erpCredentials[erpValue] || [];
  return supportedCredentials.includes(credentialType);
};

export const getIncrementalErpCode = (erpValue) => {
  const normalized = String(erpValue ?? "")
    .toUpperCase()
    .replace(/\s+/g, "");
  const map = {
    0: "OMIE",
    2: "NIBO",
    5: "CONTAAZULV2",
    OMIE: "OMIE",
    NIBO: "NIBO",
    CONTAAZULV2: "CONTAAZULV2",
  };
  return map[normalized] || null;
};

export const canUseIncrementalByErp = (erpValue) => {
  return !!getIncrementalErpCode(erpValue);
};

export const isData4companyEmail = (email) => {
  return (email || "").toLowerCase().endsWith("@data4company.com.br");
};

export const getIncrementalCooldownState = ({
  lastRequestIncAt,
  now = Date.now(),
  cooldownMs = 60 * 60 * 1000,
} = {}) => {
  if (!lastRequestIncAt) {
    return {
      bypass: false,
      isBlocked: false,
      remainingMs: 0,
    };
  }

  // O backend grava/retorna a data em UTC (mesmo sem o sufixo "Z").
  // Interpretamos explicitamente como UTC para evitar que o navegador
  // trate a string como horário local e infle o tempo restante.
  const lastRequestTimestamp = DateTime.fromISO(lastRequestIncAt, {
    zone: "utc",
  }).toMillis();
  if (Number.isNaN(lastRequestTimestamp)) {
    return {
      bypass: false,
      isBlocked: false,
      remainingMs: 0,
    };
  }

  const elapsed = now - lastRequestTimestamp;
  const remaining = cooldownMs - elapsed;

  if (remaining <= 0) {
    return {
      bypass: false,
      isBlocked: false,
      remainingMs: 0,
    };
  }

  return {
    bypass: false,
    isBlocked: true,
    remainingMs: remaining,
  };
};

export const formatIncrementalCountdown = (remainingMs) => {
  const totalSeconds = Math.max(0, Math.ceil((remainingMs || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (value) => String(value).padStart(2, "0");
  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(minutes)}:${pad(seconds)}`;
};

export const isSynchronizationConsolidated = (sync) => {
  const syncType =
    sync?.type ?? sync?.Type ?? sync?.syncType ?? sync?.SyncType ?? null;
  return String(syncType ?? "").trim() === "2";
};

export const getSynchronizationLastStatus = (sync) => {
  return (
    sync?.lastStatus ??
    sync?.LastStatus ??
    sync?.response?.lastStatus ??
    sync?.response?.LastStatus ??
    sync?.Response?.lastStatus ??
    sync?.Response?.LastStatus ??
    sync?.status ??
    sync?.Status ??
    null
  );
};

const estaSincronizando = (sync) =>
  String(getSynchronizationLastStatus(sync) ?? "")
    .trim()
    .toLowerCase()
    .includes("sincronizando");

export const getSynchronizationStatusLabel = (sync) => {
  const isNotConfigured =
    sync?.isConfigured === false || sync?.IsConfigured === false;

  // Uma carga em andamento vence "Requer atenção": enquanto o integrador está
  // trabalhando não há nada que o usuário deva fazer, e dizer o contrário fazia
  // parecer que o processamento tinha falhado.
  if (isNotConfigured && !estaSincronizando(sync)) {
    return "Requer atenção";
  }

  return getSynchronizationLastStatus(sync);
};

export const getSynchronizationStatusMessage = (sync) => {
  const isNotConfigured =
    sync?.isConfigured === false || sync?.IsConfigured === false;

  if (isNotConfigured && !estaSincronizando(sync)) {
    switch (Number(sync?.erp)) {
      case 0:
        return "Omie não configurado";
      case 5:
        return "Conta azul não configurado";
      case 2:
        return "Nibo não configurado";
      case 3:
        return "Excel não configurado";
      case 1:
        return "Conta Azul não configurado";
      default:
        return "ERP não configurado";
    }
  }

  return (
    sync?.lastStatusMessage ??
    sync?.LastStatusMessage ??
    sync?.response?.lastStatusMessage ??
    sync?.response?.LastStatusMessage ??
    sync?.Response?.lastStatusMessage ??
    sync?.Response?.LastStatusMessage ??
    "Sem detalhes"
  );
};

export const getSynchronizationStatusConfig = (statusValue) => {
  const normalizedStatus = String(statusValue || "")
    .trim()
    .toLowerCase();

  if (normalizedStatus.includes("sincronizando")) {
    return {
      color: "processing",
      type: "processing",
    };
  }

  if (normalizedStatus.includes("sucesso")) {
    return {
      color: "success",
      type: "success",
    };
  }

  if (normalizedStatus.includes("pendente")) {
    return {
      color: "warning",
      type: "warning",
    };
  }

  if (
    normalizedStatus.includes("erro") ||
    normalizedStatus.includes("falha")
  ) {
    return {
      color: "error",
      type: "error",
    };
  }

  return {
    color: "default",
    type: "default",
  };
};

export const isIncrementalAllowedByStatus = (statusValue) => {
  const normalizedStatus = String(statusValue ?? "")
    .trim()
    .toLowerCase();
  if (!normalizedStatus) {
    return true;
  }

  return (
    normalizedStatus.includes("sucesso") ||
    normalizedStatus.includes("success") ||
    normalizedStatus.includes("erro") ||
    normalizedStatus.includes("error") ||
    normalizedStatus.includes("falha") ||
    normalizedStatus.includes("failure") ||
    normalizedStatus.includes("failed")
  );
};
